import React, { useEffect, useState } from "react";
import { IoIosPersonAdd } from "react-icons/io";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import Swal from "sweetalert2";
import RoleBasedComponent from "../../../components/RoleBasedComponent";
import { showApiErrors } from "../../../utils/helpers";
import { ArrowLeft, UserPlus } from "lucide-react";

const NewUser = () => {
  const { requestWithAuth } = useAuth();
  const [newUserData, setNewUserData] = useState({});
  const [creatingUser, setCreatingUser] = useState(false);
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setNewUserData({ ...newUserData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newUserData.first_name?.trim()) {
      newErrors.first_name = "First name is required";
    }
    if (!newUserData.last_name?.trim()) {
      newErrors.last_name = "Last name is required";
    }
    if (!newUserData.branch) {
      newErrors.branch = "Branch is required";
    }
    if (!newUserData.role) {
      newErrors.role = "Role is required";
    }
    if (!newUserData.username?.trim()) {
      newErrors.username = "Username is required";
    }
    if (!newUserData.password?.trim()) {
      newErrors.password = "Password is required";
    } else if (newUserData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?paginated=false&status=Active`,
        "GET"
      );
      setBranches(data);
    } catch (error) {
      toast.error("Failed to fetch branches. Please try again later.");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setCreatingUser(true);
    const body = JSON.stringify(newUserData);
    try {
      await requestWithAuth("/users/accounts/", "POST", body, {
        "Content-Type": "application/json",
      });
      setCreatingUser(false);
      Swal.fire({
        title: "Success",
        icon: "success",
        text: "User Created Successfully",
      }).then(() => {
        window.location.href = "/administration/users";
      });
    } catch (error) {
      showApiErrors(error);
      setCreatingUser(false);
    }
  };
  useEffect(() => {
    fetchBranches();
  }, []);

  const renderField = (
    label,
    name,
    type = "text",
    required = false,
    options = null
  ) => (
    <div>
      <label
        htmlFor={name}
        className="block text-sm font-medium text-gray-700 mb-2"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {options ? (
        <select
          name={name}
          id={name}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all bg-white ${errors[name]
            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
            : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
            }`}
        >
          <option value="">Select {label}</option>
          {options}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          id={name}
          placeholder={`Enter ${label.toLowerCase()}`}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${errors[name]
            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
            : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
            }`}
        />
      )}
      {errors[name] && (
        <p className="mt-1.5 text-xs text-red-600">{errors[name]}</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors group"
          >
            <ArrowLeft
              size={20}
              className="group-hover:-translate-x-1 transition-transform"
            />
            <span className="text-sm font-medium">Back to Users</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>System</span>
                <span>/</span>
                <span>Users</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Create</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Create New User
              </h1>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <form onSubmit={(e) => createUser(e)} className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <UserPlus className="text-blue-500" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  User Information
                </h2>
                <p className="text-sm text-gray-500">
                  Enter the details for the new system user
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField("First Name", "first_name", "text", true)}
                {renderField("Last Name", "last_name", "text", true)}
              </div>
            </div>

            {/* Work Information */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Work Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField(
                  "Branch",
                  "branch",
                  "select",
                  true,
                  branches

                    .map((branch, index) => (
                      <option key={index} value={branch.id}>
                        {branch.name}
                      </option>
                    ))
                )}
                {renderField(
                  "Role",
                  "role",
                  "select",
                  true,
                  <>
                    <option value="dispenser">Dispenser</option>
                    <option value="branch_manager">Branch Manager</option>
                    <option value="it_auditor">IT Auditor</option>
                    <option value="warehouse_manager">Warehouse Manager</option>
                    <option value="stores">Stores</option>
                    <option value="admin">Administrator</option>
                    <RoleBasedComponent allowedRoles={["superadmin"]}>
                      <option value="superadmin">Super Admin</option>
                    </RoleBasedComponent>
                  </>
                )}
              </div>
            </div>

            {/* Account Information */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Account Credentials
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField("Username", "username", "text", true)}
                {renderField("Password", "password", "password", true)}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Password must be at least 6 characters long
              </p>
            </div>
          </div>

          {/* Form Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleBack}
                disabled={creatingUser}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              {!creatingUser ? (
                <button
                  type="submit"
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                >
                  Create User
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-400 rounded-lg cursor-not-allowed flex items-center gap-2"
                >
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Help Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-900">
                User Account Security
              </h3>
              <p className="mt-1 text-sm text-blue-700">
                Make sure to use a strong password. The user will be able to
                change their password after first login.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewUser;
